/* Agrupamento das linhas de idioma em VERSÕES + derivação da vigência (ADR-0774). O endpoint de
   gestão devolve uma linha por (version, locale); a UI raciocina por VERSÃO porque status e vigência
   são propriedades da VERSÃO — o publish move todas as linhas de (docKey, version) de uma vez.

   Três regras moram aqui e em nenhum outro lugar da superfície:

   1. `version` nunca é calculado no cliente: só é LIDO das linhas. O próximo número é do servidor
      (`max+1`), então não existe aritmética de versão aqui — nem um `+ 1` sequer.
   2. Uma versão conta como PUBLICADA se QUALQUER linha estiver publicada. Conservador de propósito:
      versão publicada é imutável, e oferecer "editar" numa versão com uma linha publicada faria o
      PUT bater em 409. Estado misto não deveria existir (o publish é por versão), mas se existir a
      UI erra para o lado de NÃO oferecer edição.
   3. Vigência é DERIVADA, nunca lida de uma coluna: `current` é a maior versão publicada cujo
      `effectiveAt` já passou — a mesma conta que o servidor faz na leitura pública. Sem isso a UI
      não mostraria a diferença entre `publishedAt` (ato administrativo) e `effectiveAt` (fato
      jurídico), e "publicar hoje, vigorar em 30 dias" deixaria de existir na prática. */
import type { LegalStatus, LegalVersion } from '@planuze/admin-contracts';
import { LEGAL_FALLBACK_LOCALE, LEGAL_LOCALES } from '@planuze/admin-contracts';

/** Situação da versão perante o público. `current` = é a que a leitura pública serve agora. */
export type LegalLifecycle = 'draft' | 'scheduled' | 'current' | 'superseded';

/** Uma VERSÃO: o conjunto das linhas de idioma que compartilham (docKey, version). */
export interface LegalVersionGroup {
  readonly version: number;
  readonly status: LegalStatus;
  readonly lifecycle: LegalLifecycle;
  readonly effectiveAt: string | null;
  readonly publishedAt: string | null;
  /** Linhas de idioma, pt-BR primeiro (locale canônico do fallback). Nunca vazio. */
  readonly locales: readonly LegalVersion[];
}

function firstNonNull(values: readonly (string | null)[]): string | null {
  return values.find((value) => value !== null) ?? null;
}

/* pt-BR primeiro (é o fallback da leitura pública, logo o idioma canônico da edição); o resto em
   ordem alfabética para a lista não dançar entre renders. */
function compareLocales(a: string, b: string): number {
  if (a === b) return 0;
  if (a === LEGAL_FALLBACK_LOCALE) return -1;
  if (b === LEGAL_FALLBACK_LOCALE) return 1;
  return a.localeCompare(b);
}

interface PartialGroup {
  readonly version: number;
  readonly status: LegalStatus;
  readonly effectiveAt: string | null;
  readonly publishedAt: string | null;
  readonly locales: readonly LegalVersion[];
}

function isEffectiveBy(group: PartialGroup, nowMs: number): boolean {
  return group.effectiveAt !== null && new Date(group.effectiveAt).getTime() <= nowMs;
}

function lifecycleOf(
  group: PartialGroup,
  currentVersion: number | null,
  nowMs: number,
): LegalLifecycle {
  if (group.status === 'draft') return 'draft';
  if (group.version === currentVersion) return 'current';
  /* Publicada com vigência no futuro: está no banco, invisível ao público até o relógio passar. */
  if (!isEffectiveBy(group, nowMs)) return 'scheduled';
  return 'superseded';
}

/** Agrupa as linhas por versão (mais nova primeiro) e classifica a vigência de cada uma. */
export function groupLegalVersions(
  rows: readonly LegalVersion[],
  now: Date,
): readonly LegalVersionGroup[] {
  const byVersion = new Map<number, LegalVersion[]>();
  for (const row of rows) {
    const bucket = byVersion.get(row.version);
    if (bucket === undefined) byVersion.set(row.version, [row]);
    else bucket.push(row);
  }

  const partials: PartialGroup[] = [...byVersion.entries()]
    .map(([version, locales]) => ({
      version,
      status: locales.some((l) => l.status === 'published')
        ? ('published' as const)
        : ('draft' as const),
      effectiveAt: firstNonNull(locales.map((l) => l.effectiveAt)),
      publishedAt: firstNonNull(locales.map((l) => l.publishedAt)),
      locales: [...locales].sort((a, b) => compareLocales(a.locale, b.locale)),
    }))
    .sort((a, b) => b.version - a.version);

  const nowMs = now.getTime();
  let currentVersion: number | null = null;
  for (const group of partials) {
    if (group.status !== 'published' || !isEffectiveBy(group, nowMs)) continue;
    if (currentVersion === null || group.version > currentVersion) currentVersion = group.version;
  }

  return partials.map((group) => ({
    ...group,
    lifecycle: lifecycleOf(group, currentVersion, nowMs),
  }));
}

/** Linha canônica da versão (pt-BR quando existe) — fonte do título exibido na lista. */
export function primaryLegalRow(group: LegalVersionGroup): LegalVersion | undefined {
  return group.locales.find((row) => row.locale === LEGAL_FALLBACK_LOCALE) ?? group.locales[0];
}

/** Idiomas canônicos ausentes na versão. Só existem no nascimento da versão (o update é por linha e
    não cria linha), então a superfície precisa avisar em vez de oferecer um campo que não salva. */
export function missingLegalLocales(group: LegalVersionGroup): readonly string[] {
  return LEGAL_LOCALES.filter((locale) => !group.locales.some((row) => row.locale === locale));
}
